import { useCallback, useContext, useMemo, useState } from 'react';
import { ShiftAllocationContext } from '../context/ShiftAllocationContext';
import { getAislesOptionsForBoardEntry } from './utils';
import { PROCESS_PATHS, ALLOCATION_TYPE } from '../data';
import { MOVE_ASSOCIATE } from '../action/ShiftAllocationAction';

/**
 * Separator used to encode a destination option's value as `<boardEntryId>::<processPath>`. Local to
 * this module so the value is built and parsed in one place. Deliberately NOT
 * `DROPPABLE_ID_SEPARATOR` ('~'), which encodes drag-and-drop droppable ids - a different concern.
 */
const DESTINATION_SEPARATOR = '::';

/**
 * The rules behind click-to-assign: which destinations an associate may be placed on, what an aisle
 * destination additionally requires, and the MOVE_ASSOCIATE dispatch that performs the placement.
 *
 * Shared by every surface that assigns an associate (the board's EmployeeIcon popover and the
 * Scan-To-Assign card) so eligibility, aisle validation and the dispatch shape cannot drift apart.
 *
 * @param {Object}   params.associate   the associate being placed
 * @param {Object}   params.from        MOVE_ASSOCIATE `from` for this surface, e.g.
 *                                     `{ boardEntry, processPathId }` on the board or
 *                                     `{ boardEntry }` when the current entry is derived
 * @param {Function} params.onAssigned  called after a successful dispatch (close popover/modal)
 */
export const useAssignAssociate = ({ associate, from, onAssigned }) => {
  const { shiftAllocationModel, shiftAllocationDispatch, setIsBoardUpdated } = useContext(ShiftAllocationContext);
  const [selectedDestination, setSelectedDestination] = useState(null);
  // For aisle destinations: the planner-chosen span (free min/max, not limited to open chunks).
  const [minAisle, setMinAisle] = useState(null);
  const [maxAisle, setMaxAisle] = useState(null);

  // Each option is a (boardEntry, processPath) pair. Bench/5S are excluded, and only process paths
  // the associate is TRAINED on are offered, matched case-insensitively (same convention as
  // hasPlacementIssues). An associate with no trained paths gets no destinations, and the caller
  // shows the empty state.
  const assignDestinations = useMemo(() => {
    const processInfo = (shiftAllocationModel && shiftAllocationModel.processInfo) || [];
    const trainedPaths = ((associate && associate.trainedPaths) || []).map((p) => p.toLowerCase());
    const destinations = [];
    processInfo.forEach((entry) => {
      if (entry.id === PROCESS_PATHS.BENCH || entry.id === PROCESS_PATHS.FIVE_S) {
        return;
      }
      const processPathEntries = entry.processPathEntries || [];
      if (processPathEntries.length === 0) {
        return;
      }
      const isAisleEntry = entry.allocationType === ALLOCATION_TYPE.AISLES;
      processPathEntries.forEach((ppe) => {
        if (!ppe.process || !trainedPaths.includes(ppe.process.toLowerCase())) {
          return;
        }
        const value = `${entry.id}${DESTINATION_SEPARATOR}${ppe.process}`;
        const label = processPathEntries.length > 1 ? `${entry.title} — ${ppe.process}` : entry.title;
        destinations.push({ value, label, isAisle: isAisleEntry });
      });
    });
    return destinations.sort((a, b) => a.label.localeCompare(b.label));
  }, [shiftAllocationModel, associate]);

  // The selected destination's metadata: whether it is an aisle entry, its board entry, its process.
  const selectedDestinationMeta = useMemo(() => {
    if (!selectedDestination) {
      return null;
    }
    const [toBoardEntryId, toProcessPath] = selectedDestination.split(DESTINATION_SEPARATOR);
    const dest = assignDestinations.find((d) => d.value === selectedDestination);
    const processInfo = (shiftAllocationModel && shiftAllocationModel.processInfo) || [];
    const entry = processInfo.find((e) => e.id === toBoardEntryId);
    return { isAisle: Boolean(dest && dest.isAisle), entry, processPath: toProcessPath, boardEntryId: toBoardEntryId };
  }, [selectedDestination, assignDestinations, shiftAllocationModel]);

  // Selectable aisle numbers for an aisle destination - the complete cluster range (e.g. 3..20), NOT
  // limited to currently-open chunks. The reducer expands the chosen min..max into aisles.
  const aisleOptions = useMemo(() => {
    if (!selectedDestinationMeta || !selectedDestinationMeta.isAisle || !selectedDestinationMeta.entry) {
      return [];
    }
    const clusterDefinitions = (shiftAllocationModel && shiftAllocationModel.clusterDefinitions) || [];
    return getAislesOptionsForBoardEntry(selectedDestinationMeta.entry, clusterDefinitions);
  }, [selectedDestinationMeta, shiftAllocationModel]);

  const resetSelection = useCallback(() => {
    setSelectedDestination(null);
    setMinAisle(null);
    setMaxAisle(null);
  }, []);

  // Picking a destination clears any aisle span chosen for the previous one.
  const onChangeDestination = useCallback((value) => {
    setSelectedDestination(value);
    setMinAisle(null);
    setMaxAisle(null);
  }, []);

  const hasValidAisleSpan = useMemo(() => {
    return minAisle != null && maxAisle != null && Number(minAisle) <= Number(maxAisle);
  }, [minAisle, maxAisle]);

  // Assign needs a destination; aisle destinations additionally need a valid span.
  const isAssignDisabled = useMemo(() => {
    if (!selectedDestination) {
      return true;
    }
    if (selectedDestinationMeta && selectedDestinationMeta.isAisle) {
      return !hasValidAisleSpan;
    }
    return false;
  }, [selectedDestination, selectedDestinationMeta, hasValidAisleSpan]);

  const onClickAssign = useCallback(() => {
    if (!selectedDestination) {
      return;
    }
    const [toBoardEntry, toProcessPath] = selectedDestination.split(DESTINATION_SEPARATOR);
    const isAisle = Boolean(selectedDestinationMeta && selectedDestinationMeta.isAisle);
    if (isAisle && !hasValidAisleSpan) {
      return;
    }
    setIsBoardUpdated(true);
    shiftAllocationDispatch({
      type: MOVE_ASSOCIATE,
      data: {
        associateAlias: associate.alias,
        from,
        to: {
          boardEntry: toBoardEntry,
          processPath: toProcessPath,
          // Aisle destinations carry the chosen span; the reducer expands min..max into aisles.
          ...(isAisle ? { minAisle: Number(minAisle), maxAisle: Number(maxAisle) } : {})
        }
      }
    });
    resetSelection();
    if (onAssigned) {
      onAssigned();
    }
  }, [
    selectedDestination,
    selectedDestinationMeta,
    hasValidAisleSpan,
    minAisle,
    maxAisle,
    associate,
    from,
    shiftAllocationDispatch,
    setIsBoardUpdated,
    resetSelection,
    onAssigned
  ]);

  return {
    assignDestinations,
    selectedDestination,
    selectedDestinationMeta,
    aisleOptions,
    minAisle,
    maxAisle,
    setMinAisle,
    setMaxAisle,
    onChangeDestination,
    isAssignDisabled,
    onClickAssign,
    resetSelection
  };
};

export default useAssignAssociate;
